import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { DayOfWeek, TeacherPreferences } from '../../types';

const preferencesSchema = z.object({
  preferredDays: z.array(z.enum(['monday', 'tuesday', 'wednesday', 'thursday', 'friday'])),
  avoidedDays: z.array(z.enum(['monday', 'tuesday', 'wednesday', 'thursday', 'friday'])),
  preferredPeriods: z.array(z.number()),
  avoidedPeriods: z.array(z.number()),
  maxConsecutiveLessons: z.number().min(1).max(5).optional(),
  minBreakBetweenLessons: z.number().min(0).max(3).optional(),
  preferredRooms: z.array(z.string()),
  notes: z.string().optional(),
});

type PreferenceFormData = z.infer<typeof preferencesSchema>;

interface PreferenceFormProps {
  preferences?: TeacherPreferences;
  onSubmit: (data: PreferenceFormData) => void;
  onCancel: () => void;
  maxPeriods?: number;
}

const days: DayOfWeek[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

const dayLabels: Record<DayOfWeek, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
};

export const PreferenceForm: React.FC<PreferenceFormProps> = ({ preferences, onSubmit, onCancel, maxPeriods = 5 }) => {
  const { control, handleSubmit } = useForm<PreferenceFormData>({
    resolver: zodResolver(preferencesSchema),
    defaultValues: preferences
      ? {
          preferredDays: preferences.preferredDays,
          avoidedDays: preferences.avoidedDays,
          preferredPeriods: preferences.preferredPeriods,
          avoidedPeriods: preferences.avoidedPeriods,
          maxConsecutiveLessons: preferences.maxConsecutiveLessons,
          minBreakBetweenLessons: preferences.minBreakBetweenLessons,
          preferredRooms: preferences.preferredRooms,
          notes: preferences.notes,
        }
      : {
          preferredDays: [],
          avoidedDays: [],
          preferredPeriods: [],
          avoidedPeriods: [],
          maxConsecutiveLessons: undefined,
          minBreakBetweenLessons: undefined,
          preferredRooms: [],
          notes: '',
        },
  });

  const periods = Array.from({ length: maxPeriods }, (_, i) => i + 1);

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Scheduling Preferences
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Set teacher preferences for timetable scheduling. These are soft constraints that the scheduler will try to
          accommodate.
        </Typography>

        <Grid container spacing={3}>
          {/* Day Preferences */}
          <Grid item xs={12} md={6}>
            <Controller
              name="preferredDays"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Preferred Days</InputLabel>
                  <Select
                    {...field}
                    multiple
                    value={field.value}
                    onChange={field.onChange}
                    input={<OutlinedInput label="Preferred Days" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={dayLabels[value]} size="small" color="success" />
                        ))}
                      </Box>
                    )}
                  >
                    {days.map((day) => (
                      <MenuItem key={day} value={day}>
                        {dayLabels[day]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="avoidedDays"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Avoided Days</InputLabel>
                  <Select
                    {...field}
                    multiple
                    value={field.value}
                    onChange={field.onChange}
                    input={<OutlinedInput label="Avoided Days" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={dayLabels[value]} size="small" color="error" />
                        ))}
                      </Box>
                    )}
                  >
                    {days.map((day) => (
                      <MenuItem key={day} value={day}>
                        {dayLabels[day]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>

          {/* Period Preferences */}
          <Grid item xs={12} md={6}>
            <Controller
              name="preferredPeriods"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Preferred Periods</InputLabel>
                  <Select
                    {...field}
                    multiple
                    value={field.value}
                    onChange={field.onChange}
                    input={<OutlinedInput label="Preferred Periods" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={`Period ${value}`} size="small" color="success" />
                        ))}
                      </Box>
                    )}
                  >
                    {periods.map((period) => (
                      <MenuItem key={period} value={period}>
                        Period {period}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="avoidedPeriods"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Avoided Periods</InputLabel>
                  <Select
                    {...field}
                    multiple
                    value={field.value}
                    onChange={field.onChange}
                    input={<OutlinedInput label="Avoided Periods" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={`Period ${value}`} size="small" color="error" />
                        ))}
                      </Box>
                    )}
                  >
                    {periods.map((period) => (
                      <MenuItem key={period} value={period}>
                        Period {period}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>

          {/* Workload Constraints */}
          <Grid item xs={12} md={6}>
            <Controller
              name="maxConsecutiveLessons"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Max Consecutive Lessons</InputLabel>
                  <Select
                    {...field}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    label="Max Consecutive Lessons"
                  >
                    <MenuItem value="">None</MenuItem>
                    {[1, 2, 3, 4, 5].map((num) => (
                      <MenuItem key={num} value={num}>
                        {num} lesson{num > 1 ? 's' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="minBreakBetweenLessons"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Min Break Between Lessons</InputLabel>
                  <Select
                    {...field}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    label="Min Break Between Lessons"
                  >
                    <MenuItem value="">None</MenuItem>
                    {[0, 1, 2, 3].map((num) => (
                      <MenuItem key={num} value={num}>
                        {num} period{num > 1 ? 's' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>

          {/* Additional Notes */}
          <Grid item xs={12}>
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Additional Notes"
                  multiline
                  rows={3}
                  fullWidth
                  placeholder="Enter any additional scheduling preferences or requirements..."
                />
              )}
            />
          </Grid>

          {/* Help Text */}
          <Grid item xs={12}>
            <Box sx={{ p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                💡 <strong>Tip:</strong> These preferences are soft constraints. The timetable scheduler will attempt to
                honor them but may not always be able to satisfy all preferences due to other constraints.
              </Typography>
            </Box>
          </Grid>

          {/* Form Actions */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
              <Button onClick={onCancel} variant="outlined">
                Cancel
              </Button>
              <Button type="submit" variant="contained">
                Save Preferences
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};
